// TermsOfServiceModal.jsx
import React, { useState, useContext, useEffect, useCallback } from 'react';
import ModalWrapper from './ModalWrapper';
import WebSocketContext from '../../contexts/WebSocketContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getAccountAppEndpoint, WS_ACCOUNT_TOPICS } from '../../config/wsAccountConfig';

const SECTIONS = [
  {
    num: '01',
    badgeColor: 'text-blue-400 bg-blue-500/10',
    title: 'Назначение Сервиса',
    titleEn: 'Service Purpose',
    content: (
      <ol className="list-decimal list-inside space-y-1.5 text-xs text-neutral-300 leading-relaxed font-normal">
        <li>StormHack предоставляет специализированные технические инструменты для этичной оценки безопасности и устойчивости ИСКЛЮЧИТЕЛЬНО тех серверов и инфраструктуры, которыми вы владеете, управляете или в отношении которых имеете предварительное ПИСЬМЕННОЕ разрешение собственника.</li>
        <li>Сервис НЕ предназначен для несанкционированного доступа, нарушения работы чужих систем, получения привилегий или иных противоправных действий.</li>
      </ol>
    )
  },
  {
    num: '02',
    badgeColor: 'text-emerald-400 bg-emerald-500/10',
    title: 'Аккаунт, возраст и согласие',
    titleEn: 'Account, Age & Consent',
    content: (
      <ol className="list-decimal list-inside space-y-1.5 text-xs text-neutral-300 leading-relaxed font-normal">
        <li>Регистрируясь и запуская модули, вы подтверждаете, что вам исполнилось 18 лет и вы полностью дееспособны.</li>
        <li>Перед началом любых проверок вы гарантируете, что обладаете действительным письменным разрешением на соответствующие действия.</li>
        <li>Вы обязуетесь предоставлять точную информацию и не передавать данные авторизации третьим лицам.</li>
      </ol>
    )
  },
  {
    num: '03',
    badgeColor: 'text-amber-400 bg-amber-500/10',
    title: 'Допустимое использование и ограничения',
    titleEn: 'Acceptable Use & Restrictions',
    content: (
      <div className="space-y-2 text-xs text-neutral-300 leading-relaxed">
        <p>Разрешено использовать функционал платформы исключительно на авторизованных ресурсах. Запрещается:</p>
        <ul className="list-disc list-inside ml-2 space-y-1 text-neutral-400">
          <li>обходить механизмы аутентификации или авторизации без прямого письменного согласия;</li>
          <li>нарушать доступность, целостность или конфиденциальность чужих вычислительных систем;</li>
          <li>применять инструменты для подбора учётных данных без предварительного согласования с владельцем;</li>
          <li>загружать, распространять вредоносное ПО, эксплойты или ботнет-клиенты;</li>
          <li>нарушать применимые законы о защите информации и персональных данных.</li>
        </ul>
      </div>
    )
  },
  {
    num: '04',
    badgeColor: 'text-purple-400 bg-purple-500/10',
    title: 'Описание инструментов и методов',
    titleEn: 'Tools & Methods Description',
    content: (
      <div className="space-y-1.5 text-xs text-neutral-300 leading-relaxed">
        <p>Наименования инструментов приведены для обозначения технического назначения в рамках легитимного стресс-тестирования и аудита:</p>
        <ul className="list-disc list-inside ml-2 space-y-1 text-neutral-400">
          <li><strong className="text-neutral-200">«RCON Аудит»</strong> — проверка стойкости интерфейса удалённого администрирования;</li>
          <li><strong className="text-neutral-200">«Проверка аккаунтов»</strong> — верификация собственных учётных данных;</li>
          <li><strong className="text-neutral-200">«DDoS Тестирование»</strong> — проверка отказоустойчивости и сетевых лимитов;</li>
          <li><strong className="text-neutral-200">«Боты и стресс-клиенты»</strong> — симуляция игровой нагрузки на согласованных узлах;</li>
          <li><strong className="text-neutral-200">«Zero-Day & Извлечение данных»</strong> — глубокий статический и динамический анализ сетевого протокола.</li>
        </ul>
      </div>
    )
  },
  {
    num: '05',
    badgeColor: 'text-rose-400 bg-rose-500/10',
    title: 'Ответственность пользователя',
    titleEn: 'User Responsibility',
    content: (
      <ol className="list-decimal list-inside space-y-1.5 text-xs text-neutral-300 leading-relaxed font-normal">
        <li>Вы несёте полную и единоличную юридическую ответственность за правомерность и законность проведения любого аудита.</li>
        <li>Вы обязуетесь немедленно прекратить тест при обнаружении риска отказа критической инфраструктуры.</li>
        <li>Вы обязуетесь неукоснительно соблюдать нормы национального и международного законодательства.</li>
      </ol>
    )
  },
  {
    num: '06',
    badgeColor: 'text-cyan-400 bg-cyan-500/10',
    title: 'Мониторинг, логирование и отказ от гарантий',
    titleEn: 'Monitoring, Logs & Disclaimers',
    content: (
      <div className="space-y-1.5 text-xs text-neutral-300 leading-relaxed">
        <p>Платформа может фиксировать телеметрию сетевых операций для предотвращения злоупотреблений. При фиксации противоправных действий доступ может быть аннулирован без компенсации.</p>
        <p className="text-neutral-400">Сервис предоставляется по принципу «как есть» (as is). Администрация не несёт ответственности за любой прямой или косвенный ущерб, причинённый неправомерными действиями пользователя.</p>
      </div>
    )
  }
];

const TermsOfServiceModal = ({ isOpen, onClose, onAccept, onDecline, t }) => {
  const [isAgreed, setIsAgreed] = useState(false);
  const { sendMessage } = useContext(WebSocketContext);
  const langContext = useLanguage();

  useEffect(() => {
    if (isOpen) setIsAgreed(false);
  }, [isOpen]);

  const handleAccept = useCallback(() => {
    if (isAgreed) {
      if (sendMessage) {
        sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.ACCEPT_TERMS), {
          acceptedAt: new Date().toISOString(),
          termsVersion: '13.10.2025'
        });
      }
      onAccept?.();
    }
  }, [isAgreed, sendMessage, onAccept]);

  const handleDecline = useCallback(() => {
    setIsAgreed(false);
    if (onDecline) {
      onDecline();
    } else if (onClose) {
      onClose();
    }
  }, [onDecline, onClose]);

  const isEn = (typeof t === 'function' ? t('home') === 'Home' : false) || langContext?.language === 'en';

  return (
    <ModalWrapper isOpen={isOpen} onClose={handleDecline} maxWidth="max-w-[650px]" contentPadding="p-3 sm:p-4">
      <div className="flex flex-col text-white font-sans select-text" style={{ maxHeight: 'calc(90vh - 24px)' }}>

        {/* ═══ Header ═══ */}
        <div className="flex items-center justify-between pb-2.5 mb-2 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <svg viewBox="0 0 24 24" className="w-5 h-5 sm:w-6 sm:h-6 stroke-white fill-none shrink-0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-[0.95rem] sm:text-[1.05rem] font-bold text-white tracking-tight leading-tight truncate">
                  {t?.('termsOfService') || (isEn ? 'Terms of Service' : 'Пользовательское соглашение')}
                </h2>
                <span className="text-[9px] sm:text-[10px] uppercase font-mono tracking-wider font-semibold px-2 py-0.5 rounded-full bg-white/[0.06] text-white/60 shrink-0">
                  Legal
                </span>
              </div>
              <p className="text-[10px] sm:text-[11px] text-white/40 mt-0.5 font-mono">
                StormHack Security Policy • v13.10.2025
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleDecline}
            aria-label="Close"
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-white/[0.03] hover:bg-white/[0.08] flex items-center justify-center text-white/40 hover:text-white transition-colors active:scale-90 shrink-0 cursor-pointer ml-2"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 stroke-current fill-none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ═══ Scrollable Body ═══ */}
        <div className="flex-1 overflow-y-auto overscroll-contain pr-1 min-h-0 space-y-2 custom-terms-scroll">
          
          {/* Introductory notice banner */}
          <div className="rounded-xl p-2.5 sm:p-3 bg-white/[0.025] relative overflow-hidden">
            <div className="flex items-start gap-2.5">
              <svg viewBox="0 0 24 24" className="w-5 h-5 stroke-white/80 fill-none shrink-0 mt-0.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <div>
                <h3 className="text-xs font-bold text-white tracking-wide uppercase">
                  {isEn ? 'Mandatory Audit Agreement' : 'Обязательное соглашение об аудите'}
                </h3>
                <p className="text-xs text-white/60 mt-0.5 leading-relaxed">
                  {isEn
                    ? 'Access to StormHack modules is granted strictly under the condition of full and unconditional acceptance of ethical testing terms. Any test against unauthorized targets is prohibited.'
                    : 'Запуск любых модулей платформы StormHack возможен только при полном и безусловном согласии с правилами этичного аудита. Тестирование чужих ресурсов без явного разрешения владельцев запрещено.'}
                </p>
              </div>
            </div>
          </div>

          {/* Clauses list */}
          <div className="space-y-1.5">
            {SECTIONS.map((sec) => (
              <div
                key={sec.num}
                className="rounded-xl px-2.5 py-2 sm:px-3 sm:py-2.5 bg-white/[0.02] hover:bg-white/[0.035] transition-colors duration-150"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`px-1.5 py-0.5 rounded-md font-mono font-bold text-[10px] sm:text-[10.5px] ${sec.badgeColor} shrink-0`}>
                    {sec.num}
                  </span>
                  <h4 className="text-xs font-bold text-white tracking-tight">
                    {isEn ? sec.titleEn : sec.title}
                  </h4>
                </div>
                <div className="pl-0.5 text-white/75">
                  {sec.content}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Alert / Warning */}
          <div className="rounded-xl p-2.5 sm:p-3 bg-rose-500/[0.06] relative overflow-hidden">
            <div className="flex items-start gap-2.5">
              <svg viewBox="0 0 24 24" className="w-5 h-5 stroke-rose-400 fill-none shrink-0 mt-0.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div className="text-xs text-rose-200/90 leading-relaxed">
                <p className="font-bold text-rose-300 mb-0.5">
                  {isEn ? 'Strict Legal Warning' : 'Предупреждение об ответственности'}
                </p>
                <ul className="space-y-0.5 text-[11px] sm:text-[11.5px] text-rose-200/80">
                  <li>• Мы категорически не предоставляем услуги платных атак («DDoS-for-hire»), ботнеты или вредоносный код.</li>
                  <li>• Вы несёте полную индивидуальную ответственность перед законом за любые совершенные действия.</li>
                </ul>
              </div>
            </div>
          </div>

        </div>

        {/* ═══ Footer & Actions ═══ */}
        <div className="pt-2.5 mt-2 border-t border-white/[0.06] shrink-0 flex flex-col gap-2.5">
          
          {/* Custom Sleek Apple Checkbox */}
          <label
            onClick={() => setIsAgreed(v => !v)}
            className="flex items-start sm:items-center gap-2.5 cursor-pointer group select-none py-1.5 px-1 rounded-lg transition-colors hover:bg-white/[0.02]"
          >
            <div
              className={`w-4 h-4 rounded-md flex items-center justify-center transition-all duration-200 shrink-0 mt-0.5 sm:mt-0 active:scale-90 border ${
                isAgreed
                  ? 'bg-white border-white text-black shadow-[0_0_12px_rgba(255,255,255,0.35)]'
                  : 'bg-white/[0.04] border-white/20 group-hover:border-white/40 text-transparent'
              }`}
            >
              <svg viewBox="0 0 24 24" className="w-3 h-3 stroke-black fill-none" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <span className={`text-[11.5px] sm:text-xs leading-tight transition-colors ${isAgreed ? 'text-white' : 'text-white/60 group-hover:text-white/80'}`}>
              {t?.('iAcceptTerms') || (isEn ? 'I have read and agree to the Terms of Service' : 'Я подтверждаю, что ознакомлен и принимаю условия Пользовательского соглашения')}
            </span>
          </label>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleDecline}
              className="flex-1 py-2 sm:py-2.5 px-3 sm:px-4 rounded-xl bg-white/[0.05] hover:bg-white/[0.09] active:bg-white/[0.03] text-xs font-semibold text-white/70 hover:text-white transition-all duration-150 active:scale-[0.98] cursor-pointer text-center min-h-[40px] flex items-center justify-center"
            >
              {t?.('cancel') || (isEn ? 'Decline & Exit' : 'Отклонить')}
            </button>

            <button
              type="button"
              onClick={handleAccept}
              disabled={!isAgreed}
              className={`flex-1 py-2 sm:py-2.5 px-3 sm:px-4 rounded-xl text-xs font-bold transition-all duration-200 flex items-center justify-center active:scale-[0.98] cursor-pointer min-h-[40px] ${
                isAgreed
                  ? 'bg-white text-black hover:bg-neutral-200 shadow-[0_4px_20px_rgba(255,255,255,0.2)]'
                  : 'bg-white/[0.04] text-white/20 cursor-not-allowed pointer-events-none'
              }`}
            >
              {t?.('confirm') || (isEn ? 'Accept & Continue' : 'Принять и продолжить')}
            </button>
          </div>

        </div>

      </div>
    </ModalWrapper>
  );
};

export default TermsOfServiceModal;